import React, { useCallback, useState } from "react";
import MobileSecViewsHeader from "../../components/MobileSecViewsHeader";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { useNavigate, useParams } from "react-router-dom";
import { User } from "../../api/models/user-model";
import api, { CreateUserPayload } from "../../api";
import { Button, CircularProgress, Divider } from "@mui/material";
import { selectUser, selectUserRole } from "../../slices/user";
import { showError } from "../../utils/helpers";
import CustomIconButton from "../../components/CustomIconButton";
import dayjs from "dayjs";
import DeleteUserModal from "../../components/Modals/DeleteUserModal";
import ResendConfirmationEmailModal from "../../components/Modals/ResendConfirmationEmailModal";

const MobileUserDetails = () => {
  const labels = useAppSelector(selectLabels);
  const currentUser = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const userRoleItem = user?.role?.name.slice(0, -2);

  const { id: selectedUserId } = useParams<{ id: string }>();

  const disabledFunctions = currentUser?.id === user?.id;

  const getUser = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.userByIdRequestTwo(Number(selectedUserId));
      setUser(response);
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, labels.generic_error, selectedUserId]);

  React.useEffect(() => {
    getUser();
  }, [getUser]);

  const blockUser = React.useCallback(
    async (userId: number) => {
      try {
        const payload = {
          blocked: true,
        };
        const response = await api.updateUser(userId, payload);
        if (response) {
          getUser();
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    },
    [dispatch, getUser, labels.generic_error]
  );

  const unblockUser = React.useCallback(
    async (userId: number) => {
      try {
        const payload = {
          blocked: false,
        };
        const response = await api.updateUser(userId, payload);
        if (response) {
          getUser();
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    },
    [dispatch, getUser, labels.generic_error]
  );

  const deleteUser = async () => {
    if (user) {
      try {
        setIsLoading(true);
        const formattedDate = dayjs().format("YYYY-MM-DD");
        const payload: CreateUserPayload = {
          deleted_at: formattedDate,
        };
        const response = await api.updateUser(user?.id, payload);
        if (response) {
          setShowDeleteUserModal(false);
          navigate("/users");
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  const resendConfirmationEmail = async () => {
    if (!user?.email) return;

    try {
      setIsResendingEmail(true);
      await api.resendConfirmationEmail({
        email: user.email,
      });
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      throw error;
    } finally {
      setIsResendingEmail(false);
    }
  };

  return (
    <div className="w-100 h-100 border-box p-8 scroll">
      <DeleteUserModal
        showDeleteUserModal={showDeleteUserModal}
        setShowDeleteUserModal={setShowDeleteUserModal}
        deleteFunction={deleteUser}
        userToDelete={user}
      />
      <ResendConfirmationEmailModal
        open={showResendModal}
        onClose={() => setShowResendModal(false)}
        onConfirm={resendConfirmationEmail}
        user={user}
        isLoading={isResendingEmail}
      />
      <div className="mb-4">
        <MobileSecViewsHeader title={labels.user_details} route={"/users"} />
      </div>
      {isLoading ? (
        <div className="w-100 h-100 flex justify-content-center align-items-center">
          <CircularProgress />
        </div>
      ) : (
        <div className="w-100 border-box flex flex-dir-col gap-4">
          <div
            className="w-100 border-box flex flex-dir-col align-items-center gap-6 p-8"
            style={{
              backgroundColor: "white",
              border: "1px #e0e0e0 solid",
              borderRadius: 6,
            }}
          >
            <div className="w-100 border-box">
              {user?.name && (
                <p className="body-text-xl font-w-500">{user?.name}</p>
              )}
              <Divider className="w-100 mt-4" />
              <div className="w-100 border-box flex align-items-center justify-content-between gap-2 mt-8">
                <div className="flex flex-dir-col align-items-start gap-2">
                  <p
                    className="font-14 font-w-500 subtitle-color uppercase"
                    style={{
                      color: user?.confirmed
                        ? user?.blocked
                          ? "#c2170e"
                          : "#15ab0f"
                        : "",
                    }}
                  >
                    {user?.confirmed
                      ? user?.blocked
                        ? labels.blocked
                        : labels.active
                      : labels.pending}
                  </p>
                  {!user?.confirmed && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setShowResendModal(true)}
                      sx={{
                        fontSize: 11,
                        minWidth: 0,
                        padding: "2px 0",
                        textTransform: "none",
                        lineHeight: 1.2,
                      }}
                    >
                      {labels.resend_confirmation_email}
                    </Button>
                  )}
                </div>
                <div className="border-box flex align-items-center justify-content-end gap-6">
                  <CustomIconButton
                    icon="material-symbols-light:delete"
                    onClick={
                      user?.id ? () => setShowDeleteUserModal(true) : () => {}
                    }
                    disabled={disabledFunctions}
                  />
                  {userRole === "admin" && (
                    <>
                      <CustomIconButton
                        icon={user?.blocked ? "gg:unblock" : "gg:block"}
                        iconSize={22}
                        onClick={
                          user?.blocked
                            ? () => unblockUser(user?.id)
                            : user?.id
                            ? () => blockUser(user?.id)
                            : () => {}
                        }
                        disabled={
                          disabledFunctions
                            ? true
                            : user?.confirmed
                            ? false
                            : true
                        }
                      />
                      <CustomIconButton
                        icon="tdesign:edit-filled"
                        onClick={() =>
                          navigate(`/edit-user/${user?.id}/${userRoleItem}`)
                        }
                      />
                    </>
                  )}
                </div>
              </div>
              {user?.role && (
                <div className="w-100 border-box flex flex-dir-col gap-2 mt-8">
                  <p className="body-text-xs">{labels.role}:</p>
                  <p className="body-text-sm font-w-500">
                    {user?.role?.name.startsWith("security") &&
                      labels.security_guard}
                    {user?.role?.name.startsWith("property_admin") &&
                      labels.property_manager}
                    {user?.role?.name.startsWith("host") && labels.main_user}
                    {user?.role?.name.startsWith("secondary") &&
                      labels.secondary_user}
                    {user?.role?.name.startsWith("ciso") && labels.ciso}
                    {user?.role?.name.startsWith("visitor") &&
                      labels.recurrent_visitor}
                  </p>
                </div>
              )}
              {user?.unit && (
                <div className="w-100 border-box flex flex-dir-col gap-2 mt-8">
                  <p className="body-text-xs">{labels.unit}:</p>
                  <p className="body-text-sm font-w-500">{user?.unit?.name}</p>
                </div>
              )}
              {user?.email && (
                <div className="w-100 border-box flex flex-dir-col gap-2 mt-8">
                  <p className="body-text-xs">{labels.email}:</p>
                  <p className="body-text-sm font-w-500">{user?.email}</p>
                </div>
              )}
              {user?.phone && (
                <div className="w-100 border-box flex flex-dir-col gap-2 mt-8">
                  <p className="body-text-xs">{labels.telephone}:</p>
                  <p className="body-text-sm font-w-500">{user?.phone}</p>
                </div>
              )}
              {user?.role?.name.startsWith("security") && (
                <div className="w-100 border-box flex flex-dir-col gap-2 mt-8">
                  <p className="body-text-xs">{labels.access_points}:</p>
                  {user?.access_points.map((item, index) => (
                    <div
                      className="w-100 border-box flex flex-dir-col gap-2"
                      key={`${item?.id}-${index}`}
                    >
                      <p className="body-text-sm font-w-500">
                        {index + 1}.- {item?.name}
                      </p>
                      <p className="body-text-xs ml-10">{item?.vanue_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileUserDetails;
